import React, { useImperativeHandle, useRef, useState } from 'react'
import { ethers } from 'ethers'
import { MiningPoolAbi, tokenAbi } from '../../../../utils/config'
import { useTranslation } from 'react-i18next'
import { buildTokenStakePlan, runStakeAfterApproval } from '../../transactionPlan'
import { getSelectedWalletEthersProvider } from '../../../../utils/selectedWalletProvider'
import ConsoleDialog from '../../../StakingConsole/ConsoleDialog'
import { mapStakingError } from '../../../StakingConsole/errorMessages'
import { TransactionSuccessContent } from '../../../StakingConsole/TransactionSuccess'
import { getTransactionExplorerUrl, waitForTransaction } from '../../../StakingConsole/transactionFeedback'

export default function StakeModal(props) {
    const [approveAmountInfo, setApproveAmountInfo] = useState({})
    const approveAmountInfoRef = useRef({})
    const [currentStep, setCurrentStep] = useState(1)
    const [approveLoading, setApproveLoading] = useState(false)
    const [stakeLoading, setStakeLoading] = useState(false)
    const [errorMsg, setErrorMsg] = useState('')
    const [txResult, setTxResult] = useState(null)
    const { t } = useTranslation()

    const initProvider = () => getSelectedWalletEthersProvider()
    const handleErr = (error) => setErrorMsg(mapStakingError(error, t))

    const handleStakeLogic = async () => {
        const transactionInfo = approveAmountInfoRef.current
        setErrorMsg('')
        setStakeLoading(true)
        const requestedAmount = transactionInfo.amountBigNumber
        const contract = new ethers.Contract(transactionInfo.stakeContract, MiningPoolAbi, initProvider().getSigner())
        const plan = buildTokenStakePlan({
            requestedAmount,
            balance: transactionInfo.stakeTokenBalanceBigNumber,
            allowance: requestedAmount,
            poolAddress: transactionInfo.stakeContract,
            inviter: props.invitor,
        })
        try {
            const tx = await contract[plan.stake.method](...plan.stake.args)
            const result = await waitForTransaction(tx)
            setTxResult(result)
            setCurrentStep(3)
            props.ok && props.ok(result)
        } catch (error) {
            handleErr(error)
        }
        setStakeLoading(false)
    }

    const handleApproveLogic = async () => {
        const transactionInfo = approveAmountInfoRef.current
        setCurrentStep(1)
        setErrorMsg('')
        setApproveLoading(true)
        const approveAmount = transactionInfo.amountBigNumber
        let approved = false
        try {
            const contract = new ethers.Contract(transactionInfo.tokenAddress, tokenAbi, initProvider().getSigner())
            const allowanceAmount = await contract.allowance(transactionInfo.currentWallet, transactionInfo.stakeContract)
            const plan = buildTokenStakePlan({
                requestedAmount: approveAmount,
                balance: transactionInfo.stakeTokenBalanceBigNumber,
                allowance: allowanceAmount,
                poolAddress: transactionInfo.stakeContract,
                inviter: props.invitor,
            })
            if (plan.approval) {
                const tx = await contract[plan.approval.method](...plan.approval.args)
                await waitForTransaction(tx)
            }
            setCurrentStep(2)
            approved = true
        } catch (error) {
            setCurrentStep(1)
            handleErr(error)
            approved = false
        } finally {
            setApproveLoading(false)
        }
        return approved
    }

    const handleLogic = async () => {
        await runStakeAfterApproval({ approve: handleApproveLogic, stake: handleStakeLogic })
    }

    useImperativeHandle(props.onRef, () => ({
        setApproveInfo: (info) => new Promise((resolve) => {
            approveAmountInfoRef.current = info
            setApproveAmountInfo(info)
            setCurrentStep(1)
            setTxResult(null)
            setErrorMsg('')
            resolve()
        }),
        handleLogic,
    }))

    const step = (number, label) => <div className={`console-step ${currentStep >= number ? 'is-active' : ''} ${currentStep > number || currentStep === 3 ? 'is-complete' : ''}`}>
        <span>{currentStep > number || currentStep === 3 ? <i className="fa fa-check" aria-hidden="true" /> : number}</span>
        <strong>{label}</strong>
    </div>
    const explorerUrl = getTransactionExplorerUrl(approveAmountInfo.chainId, txResult?.txHash)
    const action = currentStep === 1
        ? <button type="button" className="console-button console-button--primary" disabled={approveLoading} onClick={handleApproveLogic}>{approveLoading ? t('授权中...') : t('授权')}</button>
        : currentStep === 2
            ? <button type="button" className="console-button console-button--primary" disabled={stakeLoading} onClick={handleStakeLogic}>{stakeLoading ? t('质押中...') : t('质押')}</button>
            : <>{explorerUrl && <a className="console-button" href={explorerUrl} target="_blank" rel="noreferrer">{t('查看交易')} <i className="fa fa-external-link-alt" /></a>}<button type="button" className="console-button console-button--primary" onClick={props.handleClose}>{t('完成')}</button></>

    return <ConsoleDialog show={props.show} onClose={props.handleClose} closeLabel={t('关闭')} title={currentStep === 3 ? t('质押成功') : t('质押')} description={currentStep === 3 ? t('交易已确认') : t('完成授权并提交质押交易')} icon={currentStep === 3 ? 'fa-check' : 'fa-rocket'} tone={currentStep === 3 ? 'success' : 'default'} className={currentStep === 3 ? 'console-dialog--transaction' : ''} footer={action}>
        <div className="console-stepper">{step(1, t('授权'))}<i />{step(2, t('质押'))}<i />{step(3, t('完成'))}</div>
        {errorMsg && <p className="console-form-error" role="alert">{errorMsg}</p>}
        {currentStep < 3 && <div className="console-transaction-pending">
            <i className={`fa ${approveLoading || stakeLoading ? 'fa-spinner loading-css' : currentStep === 2 ? 'fa-check' : 'fa-wallet'}`} aria-hidden="true" />
            <div>
                <strong>{currentStep === 1 ? t('等待钱包确认授权') : t('授权已确认')}</strong>
                <p>{currentStep === 1 ? t('授权完成后将继续提交质押交易。') : t('请在钱包中确认质押交易。')}</p>
            </div>
        </div>}
        {currentStep === 2 && props.invitor && <div className="console-info-note"><span>{t('邀请人')}</span><strong>{props.invitor}</strong></div>}
        {currentStep === 3 && <TransactionSuccessContent
            title={t('质押成功')}
            description={t('您的代币已成功质押。')}
            amount={approveAmountInfo.amount}
            symbol={approveAmountInfo.tokenSymbol}
            amountLabel={t('质押数量')}
            wallet={approveAmountInfo.currentWallet}
            txHash={txResult?.txHash}
            t={t}
        />}
    </ConsoleDialog>
}
